import React from 'react'
import { Alert, Button, Checkbox, Flex, Form, Input, InputNumber, Select } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { colorOptions } from '../../../../../utils/antd.helper'
import { COLORS } from '../../../../../utils/colors.helper'
import { isNumber } from 'lodash'
import ConceptualLabelSelect from '../../../../resources/conceptual-labels/select/ConceptualLabelSelect'
import Access from '../../../components/access/Access'
import Text from 'antd/es/typography/Text'
import { useAssignmentAccess } from '../../../components/access/Access'
import MarkdownRenderer from '../../../../../components/common/markdown-renderer'
import { TextContentRules } from '../../../components/access/access-rules/assignment-rules'
import { CATEGORY_NAME_LIMIT } from '../../../../../constants/input-limits'
import TextEditor from '../../../../../components/common/text-editor/TextEditor'
import QuillRenderer from '../../../../../components/common/text-editor/QuillRenderer'
import { Perm } from '../../../../../constants/assignment-permissions'

const CategoryForm = ({ onSubmit, initialValues, error, loading, actionName = 'Submit', extraActions = [] }) => {
    const { hasAccess: hasEditPermission } = useAssignmentAccess(TextContentRules.EDIT_GRADINGS)
    const { hasAccess: hasAutoApprovePermission } = useAssignmentAccess([Perm.CAN_EDIT])
    const { hasAccess: hasEditFeedbackPermission } = useAssignmentAccess(TextContentRules.EDIT_NAMINGS)

    return (
        <Form
            name='edit-question-form'
            autoComplete='off'
            onFinish={values => onSubmit(values)}
            requiredMark={false}
            layout='vertical'
        >
            <Form.Item
                name='name'
                label='Name'
                rules={[
                    { required: true, message: 'Please, enter name' },
                    {
                        max: CATEGORY_NAME_LIMIT,
                        message: `Category name cannot exceed ${CATEGORY_NAME_LIMIT} characters`,
                    },
                ]}
                initialValue={initialValues?.name}
            >
                <Access
                    rules={TextContentRules.EDIT_NAMINGS}
                    fallback={<Text>{initialValues?.name || 'No name provided'}</Text>}
                >
                    <Input
                        placeholder='Enter name'
                        maxLength={CATEGORY_NAME_LIMIT}
                        showCount
                        data-test='category-name-input'
                    />
                </Access>
            </Form.Item>
            <Form.Item
                label='Feedback'
                name='feedback'
                initialValue={initialValues?.feedback}
                rules={[{ required: true, message: 'Please, enter feedback' }]}
            >
                <Access
                    rules={TextContentRules.EDIT_NAMINGS}
                    fallback={<Text>{initialValues?.feedback || 'No feedback provided'}</Text>}
                >
                    <TextArea rows={4} placeholder='Enter feedback' data-test='category-feedback-input' />
                </Access>
            </Form.Item>
            <Form.Item
                label='Instructions for Personalized Feedback'
                name='philosophyText'
                initialValue={initialValues?.philosophyText}
                tooltip='These instructions will guide the AI when generating personalized feedback for students in this category'
                getValueFromEvent={value => value}
            >
                <Access
                    rules={TextContentRules.EDIT_GRADINGS}
                    fallback={
                        <QuillRenderer htmlContent={initialValues?.philosophyText || 'No instructions provided'} />
                    }
                >
                    <TextEditor
                        placeholder='Enter instructions for personalized feedback generation...'
                        readOnly={!hasEditFeedbackPermission}
                        data-test='category-philosophy-text-input'
                    />
                </Access>
            </Form.Item>
            <Form.Item
                label='Description'
                name='description'
                initialValue={initialValues?.description}
                rules={[{ required: true, message: 'Please, enter criteria for assigning to this category' }]}
                tooltip='Markdown is supported: # Heading, **Bold text**, - List item, 1. Numbered item'
            >
                <Access
                    rules={TextContentRules.EDIT_GRADINGS}
                    fallback={<MarkdownRenderer content={initialValues?.description || 'No description provided'} />}
                >
                    <TextArea
                        rows={4}
                        placeholder='Enter criteria for assigning to this category'
                        data-test='category-description-input'
                    />
                </Access>
            </Form.Item>
            <Flex justify='space-between'>
                <Form.Item
                    label='Color'
                    name='colorId'
                    initialValue={isNumber(initialValues?.colorId) ? initialValues?.colorId % COLORS.length : 0}
                >
                    <Access
                        rules={TextContentRules.EDIT_NAMINGS}
                        fallback={
                            <Text>
                                {colorOptions.find(opt => opt.value === initialValues?.colorId)?.label ||
                                    'No color selected'}
                            </Text>
                        }
                    >
                        <Select
                            options={colorOptions}
                            popupMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentNode}
                            showSearch
                            optionFilterProp='label'
                            data-test='category-color-input'
                        />
                    </Access>
                </Form.Item>
                <Form.Item
                    label='Grade'
                    name='grade'
                    rules={[
                        { required: true, message: 'Please, enter grade' },
                        {
                            type: 'number',
                            message: 'Please, input a number',
                        },
                    ]}
                    initialValue={initialValues?.grade}
                >
                    <Access
                        rules={TextContentRules.EDIT_GRADINGS}
                        fallback={<Text>{initialValues?.grade ?? 'No grade set'}</Text>}
                    >
                        <InputNumber data-test='category-grade-input' />
                    </Access>
                </Form.Item>
            </Flex>
            <Form.Item
                name='conceptualLabels'
                label='Conceptual Labels'
                initialValue={initialValues?.conceptualLabels}
                tooltip='Conceptual labels identify the key  principles to be demonstrated in student work. Select labels that connect specific work patterns to broader educational concepts the student demonstrates or needs to develop.'
            >
                {!hasEditPermission ? (
                    <Text>
                        {initialValues?.conceptualLabels?.length
                            ? initialValues.conceptualLabels.map(label => label.name).join(', ')
                            : 'No conceptual labels selected'}
                    </Text>
                ) : (
                    <ConceptualLabelSelect disabled={!hasEditPermission} />
                )}
            </Form.Item>
            <Form.Item
                name='autoApprove'
                valuePropName='checked'
                initialValue={initialValues?.autoApprove !== undefined ? initialValues.autoApprove : true}
                tooltip='When enabled, submissions with very high AI confidence for this category can be bulk-approved by reviewers.'
            >
                <Access
                    rules={[Perm.CAN_EDIT]}
                    fallback={
                        <Text>
                            Enable auto-approve:{' '}
                            {initialValues?.autoApprove !== false ? 'Yes' : 'No'}
                        </Text>
                    }
                >
                    <Checkbox data-test='category-auto-approve-input'>
                        Enable auto-approve
                    </Checkbox>
                </Access>
            </Form.Item>
            {error && <Alert message={error} type='error' showIcon closable />}
            <Form.Item>
                {(hasEditPermission || hasEditFeedbackPermission || hasAutoApprovePermission) && (
                    <Flex justify='end' gap={8}>
                        {(hasEditPermission || hasEditFeedbackPermission) && extraActions.map((Action, index) => (
                            <React.Fragment key={index}>{Action}</React.Fragment>
                        ))}
                        <Button
                            type='primary'
                            htmlType='submit'
                            loading={loading}
                            data-test='category-form-submit-button'
                        >
                            {actionName}
                        </Button>
                    </Flex>
                )}
            </Form.Item>
        </Form>
    )
}

export default CategoryForm
